import { supabaseClient } from './supabase';
import { fetchGHGInventoryData } from '../components/charts/GHGInventorySupabase';
import { supabaseLocationService } from './supabaseLocationService';
import { PolicyScenario, fetchBaselineGHGData, type PolicyLoadProgress } from './policyImpactService';
import { 
  getCachedBaseline, 
  setCachedBaseline, 
  BaselineProjections,
  getOrCreateCalculationPromise,
  clearBaselineCache
} from './policyBaselineCache';
import { getVMTDataForGHG } from './vmtDataService';
import {
  calculateTotalElectricityConsumption,
  calculateTotalNaturalGasConsumption,
  getNaturalGasConsumptionBySector,
  getBuildingsEnergyData,
} from './buildingsEnergyDataService';
import {
  resolveBuildingEndUseAssumptions,
  electrifiableGasThermsInEfficiencySlice,
} from './buildingEndUseAssumptions';
import { fetchCarbonIntensityData } from './carbonIntensityService';
import { fetchElectricityCarbonIntensityMapForGeofips } from './carbonIntensitySeriesCore';
import {
  getBuildingsBauPathway,
  getPathwayTherms,
  getPathwayKwh,
  getPathwayRngShare,
  type BuildingsBauPathway,
  type BuildingsBauScenario,
  KWH_PER_THERM,
  NG_EMISSION_FACTOR_KG_PER_THERM,
} from './buildingsBauPathways';
import { applyEfficiencyLayer, type EfficiencyLayerOptions } from './buildingsEfficiencyModel';
import {
  getDefaultEnergyEfficiencyComponents,
  distributeEnergyEfficiencyTarget,
  hasAnyEfficiencyComponent,
  normalizeEnergyEfficiencyComponents,
} from '../types/policyImpact';

/** Year span for `generatePolicyTimeSeriesData` output (used by cap targets and related UI). */
export const POLICY_SERIES_YEAR_MIN = 2005;
export const POLICY_SERIES_YEAR_MAX = 2045;

/** Verbose VMT / EV / EMFAC tracing. Set REACT_APP_DEBUG_POLICY_TIME_SERIES=1 in .env.development.local */
const DEBUG_POLICY_TIME_SERIES =
  typeof process !== 'undefined' &&
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_DEBUG_POLICY_TIME_SERIES === '1';

const _policyTsConsole = globalThis.console;
function policyTsDebug(...args: unknown[]): void {
  if (DEBUG_POLICY_TIME_SERIES) _policyTsConsole.log(...args);
}
function policyTsWarn(...args: unknown[]): void {
  if (DEBUG_POLICY_TIME_SERIES) _policyTsConsole.warn(...args);
}

/** Step-by-step building electrification energy + bill trace. Set REACT_APP_DEBUG_BUILDING_ELECTRIFICATION=1 */
const DEBUG_BUILDING_ELECTRIFICATION =
  typeof process !== 'undefined' &&
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_DEBUG_BUILDING_ELECTRIFICATION === '1';

export interface TimeSeriesEmissionData {
  year: number;
  lightDutyEmissions: number;
  heavyDutyEmissions: number;
  naturalGasEmissions: number;
  electricityEmissions: number;
  wasteEmissions: number;
  offroadEmissions: number;
  /** County agricultural GHG (ag_emissions); 0 for cities */
  agricultureEmissions: number;
  totalEmissions: number;
  /** EV charging electricity consumption (kWh), used for cost consistency. Included in electricity sector emissions. */
  evChargingKwh?: number;
  /** Building-only electricity emissions (MT), excludes EV charging. Use for buildings cost cost-per-ton. */
  buildingElectricityEmissions?: number;
}

export interface PolicyTimeSeriesResult {
  baselineData: TimeSeriesEmissionData[];
  projectedData: TimeSeriesEmissionData[];
  location: string;
}

function clampNumber(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}


type EmfacForecastRow = {
  year: number;
  forecasting_ratio?: number;
  ghg_per_mile?: number;
  total_vmt?: number;
};

function buildEmfacInterpolator(rows: EmfacForecastRow[]) {
  const byYear = new Map<number, EmfacForecastRow>();
  const years = rows
    .map((r) => Number(r.year))
    .filter((y) => Number.isFinite(y))
    .sort((a, b) => a - b);

  rows.forEach((r) => {
    const y = Number(r.year);
    if (!Number.isFinite(y)) return;
    byYear.set(y, { ...r, year: y });
  });

  const uniqYears = Array.from(new Set(years)).sort((a, b) => a - b);

  const findBounds = (year: number): { lo?: number; hi?: number } => {
    if (uniqYears.length === 0) return {};
    if (byYear.has(year)) return { lo: year, hi: year };
    let lo: number | undefined;
    let hi: number | undefined;
    for (let i = 0; i < uniqYears.length; i++) {
      const y = uniqYears[i];
      if (y < year) lo = y;
      if (y > year) {
        hi = y;
        break;
      }
    }
    return { lo, hi };
  };

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  const interpolateNumber = (year: number, key: keyof EmfacForecastRow): number | undefined => {
    const exact = byYear.get(year);
    const exactVal = exact?.[key];
    if (typeof exactVal === 'number' && Number.isFinite(exactVal)) return exactVal;

    const { lo, hi } = findBounds(year);
    const loVal = lo !== undefined ? byYear.get(lo)?.[key] : undefined;
    const hiVal = hi !== undefined ? byYear.get(hi)?.[key] : undefined;

    // Interpolate if we have both sides
    if (
      lo !== undefined &&
      hi !== undefined &&
      lo !== hi &&
      typeof loVal === 'number' &&
      Number.isFinite(loVal) &&
      typeof hiVal === 'number' &&
      Number.isFinite(hiVal)
    ) {
      const t = (year - lo) / (hi - lo);
      return lerp(loVal, hiVal, t);
    }

    // Extrapolate using last two points (forward)
    if (lo !== undefined && hi === undefined && uniqYears.length >= 2) {
      const last = uniqYears[uniqYears.length - 1];
      const prev = uniqYears[uniqYears.length - 2];
      const lastVal = byYear.get(last)?.[key];
      const prevVal = byYear.get(prev)?.[key];
      if (
        typeof lastVal === 'number' &&
        Number.isFinite(lastVal) &&
        typeof prevVal === 'number' &&
        Number.isFinite(prevVal) &&
        last !== prev
      ) {
        const slopePerYear = (lastVal - prevVal) / (last - prev);
        return lastVal + slopePerYear * (year - last);
      }
    }

    // Extrapolate using first two points (backward)
    if (hi !== undefined && lo === undefined && uniqYears.length >= 2) {
      const first = uniqYears[0];
      const next = uniqYears[1];
      const firstVal = byYear.get(first)?.[key];
      const nextVal = byYear.get(next)?.[key];
      if (
        typeof firstVal === 'number' &&
        Number.isFinite(firstVal) &&
        typeof nextVal === 'number' &&
        Number.isFinite(nextVal) &&
        next !== first
      ) {
        const slopePerYear = (nextVal - firstVal) / (next - first);
        return firstVal - slopePerYear * (first - year);
      }
    }

    return undefined;
  };

  const getRow = (year: number): EmfacForecastRow | undefined => {
    const exact = byYear.get(year);
    if (exact) return exact;
    if (uniqYears.length === 0) return undefined;
    // Build a synthetic row with interpolated values when missing
    const forecasting_ratio = interpolateNumber(year, 'forecasting_ratio');
    const ghg_per_mile = interpolateNumber(year, 'ghg_per_mile');
    const total_vmt = interpolateNumber(year, 'total_vmt');
    if (
      typeof forecasting_ratio !== 'number' &&
      typeof ghg_per_mile !== 'number' &&
      typeof total_vmt !== 'number'
    ) {
      return undefined;
    }
    return { year, forecasting_ratio, ghg_per_mile, total_vmt };
  };

  return { getRow, years: uniqYears };
}

async function fetchCountyPopulationByYear(location: string): Promise<Record<number, number>> {
  try {
    await supabaseLocationService.initialize();
  } catch {
    // ignore; some callers already initialized
  }

  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '').trim();
  const variations = Array.from(
    new Set([resolvedCounty, baseCountyName, `${baseCountyName} County`].filter(Boolean))
  );

  for (const loc of variations) {
    const { data, error } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('location_name', loc)
      .order('year', { ascending: true });

    if (!error && data && data.length > 0) {
      const out: Record<number, number> = {};
      data.forEach((r: any) => {
        if (r?.year && r?.population) out[Number(r.year)] = Number(r.population);
      });
      return out;
    }
  }

  return {};
}

function fillAndExtrapolatePopulation(popByYear: Record<number, number>, startYear: number, endYear: number): Record<number, number> {
  const yearsAvail = Object.keys(popByYear).map((y) => parseInt(y, 10)).filter(Number.isFinite).sort((a, b) => a - b);
  if (yearsAvail.length === 0) return {};

  const lastYear = yearsAvail[yearsAvail.length - 1];
  const prevYear = yearsAvail[yearsAvail.length - 2] ?? lastYear - 1;
  const growthPerYear =
    lastYear > prevYear ? (popByYear[lastYear] - popByYear[prevYear]) / (lastYear - prevYear) : 0;

  const filled: Record<number, number> = { ...popByYear };

  // Forward extrapolation
  for (let y = lastYear + 1; y <= endYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[lastYear] + growthPerYear * (y - lastYear)));
  }

  // Backfill earlier years (flat at earliest)
  const firstYear = yearsAvail[0];
  for (let y = startYear; y < firstYear; y++) {
    filled[y] = Math.max(1, Math.round(popByYear[firstYear]));
  }

  return filled;
}

/**
 * Fetch EMFAC county filtered data for VMT projection using forecasting_ratio
 * Uses the same approach as GHG Inventory to get ghg_per_mile values
 * @param location - Location name
 * @param emfacVersion - '2021' or '2025' to select EMFAC version
 */
async function fetchEMFACForecastingData(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<any[]> {
  // Resolve county for the city to query EMFAC at the county level
  const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
  const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
  const emfacLocation = `${baseCountyName} County`;
  
  policyTsDebug(`🔍 fetchEMFACForecastingData: location="${location}", resolvedCounty="${resolvedCounty}", emfacLocation="${emfacLocation}"`);
  
  // Select table based on EMFAC version
  const tableName = emfacVersion === '2025' ? 'emfac2025_county_filtered' : 'emfac2021_county_filtered';
  
  // Try common naming variants
  const locationVariations = [
    emfacLocation,
    baseCountyName,
    `${baseCountyName} County`,
    resolvedCounty
  ];
  
  for (const loc of locationVariations) {
    policyTsDebug(`🔍 Policy Tool: Trying EMFAC${emfacVersion} county filtered for location: ${loc}`);
    const { data, error } = await supabaseClient
      .from(tableName)
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', loc)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (error) {
      policyTsDebug(`⚠️ Policy Tool: EMFAC${emfacVersion} county filtered query error for`, loc, ':', error);
      continue;
    }
    
    if (data && data.length > 0) {
      policyTsDebug(`✅ Policy Tool: EMFAC${emfacVersion} county filtered data found for`, loc, `(${data.length} years)`);
      // Log key values for debugging
      const year2023 = data.find(d => d.year === 2023);
      const year2024 = data.find(d => d.year === 2024);
      if (year2023) policyTsDebug(`  2023: ghg_per_mile=${year2023.ghg_per_mile}, forecasting_ratio=${year2023.forecasting_ratio}`);
      if (year2024) policyTsDebug(`  2024: ghg_per_mile=${year2024.ghg_per_mile}, forecasting_ratio=${year2024.forecasting_ratio}`);
      return data;
    }
  }
  
  policyTsDebug(`Policy Tool: No EMFAC${emfacVersion} county filtered data found for any location variation:`, locationVariations);
  
  // Fallback: Try California data (same as GHG Inventory)
  const californiaVariations = ['California', 'CA', 'California State', 'State of California'];
  
  for (const californiaName of californiaVariations) {
    policyTsDebug(`Policy Tool: Trying California EMFAC${emfacVersion} county filtered: ${californiaName}`);
    const { data: californiaData, error: californiaError } = await supabaseClient
      .from(tableName)
      .select('year, forecasting_ratio, ghg_per_mile, total_vmt')
      .eq('location_name', californiaName)
      .eq('class', 'LDV')
      .gte('year', 2020)
      .lte('year', 2045)
      .order('year', { ascending: true });
    
    if (californiaError) {
      policyTsDebug(`Policy Tool: California EMFAC${emfacVersion} county filtered query error for ${californiaName}:`, californiaError);
      continue;
    }
    
    if (californiaData && californiaData.length > 0) {
      policyTsDebug(`Policy Tool: Using California EMFAC${emfacVersion} county filtered data for ${californiaName}:`, californiaData);
      return californiaData;
    }
  }
  
  policyTsDebug(`Policy Tool: No EMFAC${emfacVersion} data available`);
  return [];
}

/**
 * Calculate historical emissions (2005-2023)
 * IMPORTANT: Historical data is ALWAYS pulled directly from the sector-based GHG inventory chart.
 * The EMFAC version selector only affects projections (2024+), not historical data.
 * This ensures historical values are identical to what's shown in the GHG inventory chart.
 */
async function calculateHistoricalEmissions(location: string, emfacVersion: '2021' | '2025' = '2025'): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get the GHG inventory data - this is the source of truth for all historical emissions
    const inventoryData = await fetchGHGInventoryData(location);
    
    // Extract year-specific historical data from the inventory result
    const historicalData: TimeSeriesEmissionData[] = [];
    
    // Get the chart data which contains year-specific emissions
    const chartData = inventoryData.chartData;
    const years = chartData.labels.map((year: any) => parseInt(year));
    
    policyTsDebug('Policy Tool: Available years in GHG inventory:', years);
    policyTsDebug('Policy Tool: Using GHG inventory chart data directly (EMFAC version does not affect historical data)');
    
    // Extract datasets for ALL categories from GHG inventory chart
    // This ensures historical data matches exactly what's shown in the GHG inventory chart
    const lightDutyDataset = chartData.datasets.find((d: any) => d.label === 'Light Duty Vehicles');
    const heavyDutyDataset = chartData.datasets.find((d: any) => d.label === 'Heavy Duty Vehicles');
    const naturalGasResDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Residential');
    const naturalGasComDataset = chartData.datasets.find((d: any) => d.label === 'Natural Gas Commercial');
    const electricityResDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Residential');
    const electricityComDataset = chartData.datasets.find((d: any) => d.label === 'Electricity Commercial');
    const wasteDataset = chartData.datasets.find((d: any) => d.label === 'Waste');
    const offroadDataset = chartData.datasets.find((d: any) => d.label === 'Offroad');
    const agricultureDataset = chartData.datasets.find((d: any) => d.label === 'Agriculture');

    // Process all years from 2005 to 2023
    for (let year = 2005; year <= 2023; year++) {
      const yearIndex = years.indexOf(year);
      
      if (yearIndex !== -1) {
        // Use GHG Inventory chart data directly for ALL sources (including LDV)
        // This ensures historical data is identical to the GHG inventory chart
        const lightDutyEmissions = lightDutyDataset?.data[yearIndex] || 0;
        const heavyDutyEmissions = heavyDutyDataset?.data[yearIndex] || 0;
        
        // Combine residential and commercial natural gas emissions
        const naturalGasRes = naturalGasResDataset?.data[yearIndex] || 0;
        const naturalGasCom = naturalGasComDataset?.data[yearIndex] || 0;
        const naturalGasEmissions = naturalGasRes + naturalGasCom;
        
        // Combine residential and commercial electricity emissions
        const electricityRes = electricityResDataset?.data[yearIndex] || 0;
        const electricityCom = electricityComDataset?.data[yearIndex] || 0;
        const electricityEmissions = electricityRes + electricityCom;

        const wasteEmissions = wasteDataset?.data?.[yearIndex] || 0;
        const offroadEmissions = offroadDataset?.data?.[yearIndex] || 0;
        const agricultureEmissions = agricultureDataset?.data?.[yearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(lightDutyEmissions),
          heavyDutyEmissions: Math.round(heavyDutyEmissions),
          naturalGasEmissions: Math.round(naturalGasEmissions),
          electricityEmissions: Math.round(electricityEmissions),
          wasteEmissions: Math.round(wasteEmissions),
          offroadEmissions: Math.round(offroadEmissions),
          agricultureEmissions: Math.round(agricultureEmissions),
          totalEmissions: Math.round(
            lightDutyEmissions +
              heavyDutyEmissions +
              naturalGasEmissions +
              electricityEmissions +
              wasteEmissions +
              offroadEmissions +
              agricultureEmissions
          ),
        });
        
        if (year >= 2020 || year % 5 === 0) {
          policyTsDebug(`Policy Tool: Historical ${year} (from GHG Inventory): LD=${lightDutyEmissions}, HD=${heavyDutyEmissions}, NG=${naturalGasEmissions}, EL=${electricityEmissions}`);
        }
      } else {
        // Fallback: use latest year's data if specific year not available
        const latestYearIndex = years.length - 1;
        
        // Use latest year's data from GHG Inventory for ALL sources
        const latestLightDuty = lightDutyDataset?.data[latestYearIndex] || 0;
        const latestHeavyDuty = heavyDutyDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasRes = naturalGasResDataset?.data[latestYearIndex] || 0;
        const latestNaturalGasCom = naturalGasComDataset?.data[latestYearIndex] || 0;
        const latestElectricityRes = electricityResDataset?.data[latestYearIndex] || 0;
        const latestElectricityCom = electricityComDataset?.data[latestYearIndex] || 0;
        const latestWaste = wasteDataset?.data?.[latestYearIndex] || 0;
        const latestOffroad = offroadDataset?.data?.[latestYearIndex] || 0;
        const latestAgriculture = agricultureDataset?.data?.[latestYearIndex] || 0;
        
        historicalData.push({
          year,
          lightDutyEmissions: Math.round(latestLightDuty),
          heavyDutyEmissions: Math.round(latestHeavyDuty),
          naturalGasEmissions: Math.round(latestNaturalGasRes + latestNaturalGasCom),
          electricityEmissions: Math.round(latestElectricityRes + latestElectricityCom),
          wasteEmissions: Math.round(latestWaste),
          offroadEmissions: Math.round(latestOffroad),
          agricultureEmissions: Math.round(latestAgriculture),
          totalEmissions: Math.round(
            latestLightDuty +
              latestHeavyDuty +
              latestNaturalGasRes +
              latestNaturalGasCom +
              latestElectricityRes +
              latestElectricityCom +
              latestWaste +
              latestOffroad +
              latestAgriculture
          ),
        });
      }
    }
    
    policyTsDebug('Policy Tool: Historical emissions calculated from GHG inventory chart:', historicalData);
    return historicalData;
  } catch (error) {
    console.error('Error calculating historical emissions:', error);
    return [];
  }
}

/**
 * Project future emissions (2023-2045) using EMFAC forecasting data
 */
async function calculateProjectedEmissions(
  location: string,
  historicalData: TimeSeriesEmissionData[],
  vmtReductionPercent: number = 0,
  evAdoptionPercent: number = 0,
  currentEVPercent: number = 0,
  scenario?: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauPathway?: BuildingsBauPathway
): Promise<TimeSeriesEmissionData[]> {
  try {
    // Get EMFAC forecasting data (county-level)
    const emfacData = await fetchEMFACForecastingData(location, emfacVersion);
    const emfacInterp = buildEmfacInterpolator((emfacData || []) as EmfacForecastRow[]);

    // Get VMT data from Transportation page (includes user customizations) - same as GHG Inventory
    let geofips: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofips = await supabaseLocationService.getGeofips(location);
    } catch (error) {
      policyTsWarn('Could not get geofips for VMT data:', error);
    }

    // Buildings: use BAU pathway when provided (stock-based); otherwise raw consumption
    const buildingsEnergyData = buildingsBauPathway ? null : await getBuildingsEnergyData(location);
    let electricityIntensityGPerKwhByYear: Record<number, number> = {};
    try {
      const geofipsStr = geofips ? String(geofips) : null;
      if (geofipsStr && geofips != null) {
        const fromTable = await fetchElectricityCarbonIntensityMapForGeofips(
          supabaseClient,
          geofips,
          2005,
          2045
        );
        fromTable.forEach((v, y) => {
          electricityIntensityGPerKwhByYear[y] = v;
        });
        const ci = await fetchCarbonIntensityData(geofipsStr);
        const years = (ci.chartData?.labels || []) as any[];
        const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
        years.forEach((y: any, i: number) => {
          const yr = Number(y);
          const v = Number(data[i]);
          if (
            Number.isFinite(yr) &&
            Number.isFinite(v) &&
            v > 0 &&
            electricityIntensityGPerKwhByYear[yr] == null
          ) {
            electricityIntensityGPerKwhByYear[yr] = v;
          }
        });
      }
    } catch (e) {
      policyTsWarn('Could not fetch carbon intensity series; electricity emissions will use fallback intensity.', e);
      electricityIntensityGPerKwhByYear = {};
    }
    const g2023 = electricityIntensityGPerKwhByYear[2023] || 300; // gCO2e/kWh fallback
    const cleanElectricityYear = 2045; // SB 100 default for baseline projections
    
    // Import and use the same VMT data service as GHG Inventory
    const { getVMTDataForGHG } = await import('./vmtDataService');
    const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
    policyTsDebug('VMT data for projection:', vmtData);
    
    // Get the latest VMT value (2023) as baseline
    const latestVmtData = vmtData.find(d => d.year === 2023);
    const baselineVmt = latestVmtData?.vmt || 0;
    
    policyTsDebug('Baseline VMT (2023):', baselineVmt);
    
    // Calculate 2023 city-specific emission factor from historical GHG inventory data
    // This ensures continuity: 2023 uses city-specific EF (from GHG inventory), 2024+ uses county EF * scaling_factor
    // Formula: EF_city_2023 = emissions_2023 / VMT_2023 (from GHG inventory)
    let cityEmissionFactor2023: number | null = null;
    let countyEmissionFactor2023: number | null = null;
    let emissionFactorScalingFactor: number = 1.0;
    
    try {
      // Get 2023 historical emissions and VMT to calculate city-specific emission factor
      const historical2023 = historicalData.find(d => d.year === 2023);
      const ldv2023 = historical2023?.lightDutyEmissions || 0;
      
      if (ldv2023 > 0 && baselineVmt > 0) {
        // Calculate city-specific emission factor from GHG inventory: emissions / VMT
        cityEmissionFactor2023 = ldv2023 / baselineVmt; // tons CO2 per mile
        policyTsDebug(`🔍 City-specific EF 2023 (from GHG inventory): ${cityEmissionFactor2023.toFixed(8)} (emissions=${ldv2023.toFixed(2)} / VMT=${baselineVmt.toFixed(0)})`);
      }
      
      // Get county-level emission factor for 2023 from EMFAC data
      const emfac2023Data = emfacInterp.getRow(2023) || emfacData.find(d => d.year === 2023);
      if (emfac2023Data && emfac2023Data.ghg_per_mile) {
        countyEmissionFactor2023 = Number(emfac2023Data.ghg_per_mile);
        policyTsDebug(`🔍 County-level EF 2023 (from emfac${emfacVersion}_county_filtered): ${countyEmissionFactor2023.toFixed(8)}`);
      }
      
      // Calculate scaling factor: EF_city_2023 / EF_county_2023
      // This scaling factor will be applied to all future years to maintain city-specific adjustment
      // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
      if (cityEmissionFactor2023 && countyEmissionFactor2023 && countyEmissionFactor2023 > 0) {
        emissionFactorScalingFactor = cityEmissionFactor2023 / countyEmissionFactor2023;
        policyTsDebug(`🔍 Emission Factor Scaling Factor: ${emissionFactorScalingFactor.toFixed(6)} (city_2023=${cityEmissionFactor2023.toFixed(8)} / county_2023=${countyEmissionFactor2023.toFixed(8)})`);
      }
    } catch (error) {
      policyTsWarn('Could not calculate city-specific emission factor for scaling:', error);
    }
    
    const projectedData: TimeSeriesEmissionData[] = [];
    const historical2023 = historicalData.find(d => d.year === 2023);
    
    // Calculate fallback average emission factor from available EMFAC data
    const validEmissionFactors = emfacData.filter(d => d.ghg_per_mile > 0);
    const avgEmissionFactor = validEmissionFactors.length > 0 
      ? validEmissionFactors.reduce((sum, d) => sum + d.ghg_per_mile, 0) / validEmissionFactors.length
      : 0.0008; // Fallback emission factor (tons CO2 per mile)
    
    policyTsDebug('Policy Tool: Fallback average emission factor:', avgEmissionFactor, 'tons CO2 per mile');
    policyTsDebug('Policy Tool: Available emission factors:', validEmissionFactors.map(d => `${d.year}: ${d.ghg_per_mile}`));
    
    // If we have an EMFAC emission factor for 2023, recompute LDV 2023 with it
    // Pre-fetch baseline data and calculate grid intensity for EV policy
    let baseline: any = null;
    let gridIntensity: number | null = null;
    const CONSTANTS = {
      BASELINE_GRID_INTENSITY: 0.2,
      ANNUAL_SOLAR_HOURS: 2000,
      SOLAR_DISPLACEMENT_FACTOR: 0.85,
      EV_KWH_PER_MILE: 0.3,
      ICE_IMPROVEMENT_FACTOR: 0.95,
    };
    
    // Calculate grid intensity for EV adoption (needed for EV emission factor calculation)
    // Always calculate grid intensity if there's any EV adoption (baseline BAU ramps from 6.01% to 69.27%)
    if (evAdoptionPercent > 0 || currentEVPercent > 0) {
      try {
        baseline = await fetchBaselineGHGData(location, emfacVersion);
        const solarMult = scenario?.solarCapacityMultiplier || 1;
        const solarCapacityKw = baseline.solarCapacityKw * solarMult;
        const totalElectricityConsumption = baseline.population * 7000;
        const solarGeneration = solarCapacityKw * CONSTANTS.ANNUAL_SOLAR_HOURS;
        const solarPenetration = totalElectricityConsumption > 0 ? solarGeneration / totalElectricityConsumption : 0;
        gridIntensity = CONSTANTS.BASELINE_GRID_INTENSITY * (1 - Math.min(solarPenetration * CONSTANTS.SOLAR_DISPLACEMENT_FACTOR, 0.9));
      } catch (e) {
        policyTsWarn('Could not fetch baseline data for EV calculation:', e);
      }
    }

    // Population scaling (county) for projecting Waste / Natural Gas / Electricity
    const countyPopRaw = await fetchCountyPopulationByYear(location);
    const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);
    const basePop = countyPopByYear[2023] || 1;

    // Track cumulative VMT as we apply ratios sequentially
    // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1) (from emfac2025_county_filtered)
    // So to get VMT_2024: VMT_2024 = VMT_2023 * forecasting_ratio_2024
    // To get VMT_2025: VMT_2025 = VMT_2024 * forecasting_ratio_2025
    let currentVmt = baselineVmt;
    
    // IMPORTANT: Do NOT recalculate 2023 here - use historical data from GHG Inventory chart directly
    // The GHG Inventory chart already uses VMT data from the VMT chart (Transportation page)
    // and respects user customizations (backcasting method, data table edits, etc.)
    // Starting from 2024, we project forward using EMFAC forecasting ratios
    let loggedMissingForecastingRatio = false;
    for (let year = 2024; year <= 2045; year++) {
      
      // For years 2024+, apply forecasting ratio for THIS year
      // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
      // So to get VMT for year Y: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
      
      // Find EMFAC data for this year (for emission factor and forecasting ratio)
      const emfacYearData = emfacInterp.getRow(year) || emfacData.find(d => d.year === year);
      
      // Get 2023 historical data for continuity
      // For 2024, verify the calculation matches the expected formula:
      // emissions_2024 = VMT_2023 * forecasting_ratio_2024 * emission_factor_2024
      // This should be < emissions_2023 if both ratio and emission factor decrease
      
      // For projections (2024+), ALWAYS use forecasting ratios from emfac2025_county_filtered
      // Historical data is only to 2023. Everything after 2023 is a projection using ratios.
      // Never use actual VMT values for projections - they may have discontinuities.
      // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
      // Applied as: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
      const emfacYearRatio = emfacYearData?.forecasting_ratio;
      let projectedVmt: number;
      
      if (emfacYearRatio && emfacYearRatio > 0) {
        // Always use forecasting ratio for projections: VMT_year = VMT_(year-1) * forecasting_ratio_year
        const prevVmt = currentVmt;
        projectedVmt = currentVmt * emfacYearRatio;
        currentVmt = projectedVmt; // Update for next iteration
        
        // Debug VMT calculation for 2024-2027
        if (year >= 2024 && year <= 2027) {
          policyTsDebug(`🔍 VMT Calc ${year}: Using forecasting ratio, prevVmt=${prevVmt.toFixed(0)}, ratio_${year}=${emfacYearRatio.toFixed(6)}, projectedVmt=${projectedVmt.toFixed(0)}`);
        }
      } else {
        if (!loggedMissingForecastingRatio) {
          loggedMissingForecastingRatio = true;
          policyTsWarn(
            '⚠️ EMFAC rows missing forecasting_ratio for some years (e.g. statewide California); VMT held flat for those years.',
          );
        }
        projectedVmt = currentVmt;
      }
      
      // Use year-specific emission factor from EMFAC data (county-level), or fallback to average
      const yearEmissionFactorCounty = emfacYearData?.ghg_per_mile || avgEmissionFactor;
      
      // Apply scaling factor to maintain continuity with 2023 city-specific emission factor
      // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
      // This ensures that the city-specific adjustment from 2023 is maintained for all projection years
      // while using county-level emission factor trends for the projection
      const adjustedEmissionFactor = yearEmissionFactorCounty * emissionFactorScalingFactor;
      
      if (year === 2024) {
        policyTsDebug(`🔍 2024 EF Adjustment: EF_2024_county=${yearEmissionFactorCounty.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}, EF_2024_adjusted=${adjustedEmissionFactor.toFixed(8)}`);
        if (cityEmissionFactor2023 && countyEmissionFactor2023) {
          policyTsDebug(`🔍 Continuity check: EF_2023_city=${cityEmissionFactor2023.toFixed(8)}, EF_2023_county=${countyEmissionFactor2023.toFixed(8)}, ratio=${emissionFactorScalingFactor.toFixed(6)}`);
        }
      }
      
      // Debug emission factor for 2024-2027 to verify correct values are used
      if (year >= 2024 && year <= 2027) {
        policyTsDebug(`🔍 Emission Factor ${year}: yearEmissionFactor_county=${yearEmissionFactorCounty.toFixed(8)}, adjustedEmissionFactor=${adjustedEmissionFactor.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}, from emfacYearData=${emfacYearData?.ghg_per_mile?.toFixed(8) || 'NULL'}, avgEmissionFactor=${avgEmissionFactor.toFixed(8)}`);
      }
      
      // Calculate gradual VMT reduction percentage for this year
      // Gradual ramp: increases by targetVMTReduction% / (2045 - 2023) each year from 2024 to 2045
      // Formula: annualVMTReductionIncrease = targetVMTReduction% / (2045 - 2023)
      // For year Y: vmtReductionPercentThisYear = 0 + annualIncrease * (Y - 2023)
      const yearsTo2045 = 2045 - 2023; // Total years from 2023 to 2045 (22 years)
      const yearsElapsed = year - 2023; // Years elapsed since 2023
      const annualVMTReductionIncrease = vmtReductionPercent / yearsTo2045; // Annual increase in VMT reduction percentage
      
      // Calculate VMT reduction percentage for this year
      const vmtReductionPercentThisYear = yearsElapsed <= 0 
        ? 0 // 2023: no VMT reduction yet
        : Math.min(vmtReductionPercent, annualVMTReductionIncrease * yearsElapsed); // 2024-2045: gradual increase
      
      // Apply gradual VMT reduction from policy tool
      const reducedVmt = projectedVmt * (1 - vmtReductionPercentThisYear / 100);
      
      // Calculate EV adoption percentage for this year
      // Gradual ramp between current and target from 2023->2045, supporting both increases and decreases.
      const annualEVIncrease = (evAdoptionPercent - currentEVPercent) / yearsTo2045; // % points per year
      const evUnclamped =
        yearsElapsed <= 0 ? currentEVPercent : currentEVPercent + (annualEVIncrease * yearsElapsed);
      const evLowerBound = Math.min(currentEVPercent, evAdoptionPercent, 100);
      const evUpperBound = Math.max(currentEVPercent, evAdoptionPercent, 0);
      const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
      
      // Debug for baseline calculation (2023-2026) when evAdoptionPercent equals currentEVPercent
      if (year <= 2026 && evAdoptionPercent === currentEVPercent) {
        policyTsDebug(`🔍 Baseline Calc ${year}: evAdoptionPercent=${evAdoptionPercent}, currentEVPercent=${currentEVPercent}, annualEVIncrease=${annualEVIncrease.toFixed(6)}, evPercentThisYear=${evPercentThisYear.toFixed(2)}, yearsElapsed=${yearsElapsed}`);
      }
      
      // Calculate fleet-weighted emission factor based on EV adoption
      // IMPORTANT: For baseline BAU (evAdoptionPercent === currentEVPercent2045), use adjustedEmissionFactor directly.
      // The emission factors from EMFAC (emfac2025_county_filtered or emfac2021_county_filtered) already include
      // the baseline EV adoption that EMFAC models. We should NOT apply our own EV adoption ramp on top of it.
      // This prevents double-counting and ensures the baseline matches EMFAC's projections.
      // For 2024, adjustedEmissionFactor ensures continuity with 2023 GHG inventory value.
      let fleetEmissionFactor = adjustedEmissionFactor;
      
      // Only apply EV adoption adjustment if it differs from baseline BAU
      // For baseline BAU: evAdoptionPercent === currentEVPercent2045, so evPercentThisYear will match baseline
      // For policy scenarios: evAdoptionPercent !== currentEVPercent2045, so we apply additional EV adoption
      const isBaselineBAU = Math.abs(evAdoptionPercent - currentEVPercent) < 0.01; // Allow small floating point differences
      
      if (!isBaselineBAU && gridIntensity !== null) {
        // Calculate EV emission factor
        const evGhgPerMileTons = (gridIntensity * CONSTANTS.EV_KWH_PER_MILE) / 1000; // kg to tons
        
        // Use adjusted emission factor as base (city-specific, scaled from county-level EMFAC)
        const rawEmissionFactor = adjustedEmissionFactor;
        
        // Calculate fleet-weighted emission factor based on actual EV percentage for this year
        // Formula: fleetEF = rawEF * (1 - evPercent/100) + evEF * (evPercent/100)
        // This ensures:
        // - At baseline EV%: fleetEF = rawEmissionFactor (matches EMFAC baseline)
        // - At 100% EV: fleetEF = evGhgPerMileTons (all EVs)
        const evFraction = evPercentThisYear / 100;
        fleetEmissionFactor = rawEmissionFactor * (1 - evFraction) + evGhgPerMileTons * evFraction;
        
        // Ensure emission factor doesn't go below EV-only emissions
        fleetEmissionFactor = Math.max(evGhgPerMileTons, fleetEmissionFactor);
      }
      
      // Calculate emissions: VMT * emission factor
      // For baseline BAU: fleetEmissionFactor = yearEmissionFactor (from EMFAC, decreases over time)
      // This ensures emissions decrease as EMFAC emission factors decrease
      const lightDutyEmissions = reducedVmt * fleetEmissionFactor;
      const effectiveEmissionFactor = fleetEmissionFactor;
      
      // Debug calculation for 2023-2027 to identify bump
      if (year >= 2023 && year <= 2027) {
        const historical2023 = historicalData.find(d => d.year === 2023);
        const ldv2023 = historical2023?.lightDutyEmissions || 0;
        policyTsDebug(`🔍 Emissions Calc ${year}: reducedVmt=${reducedVmt.toFixed(0)}, fleetEmissionFactor=${fleetEmissionFactor.toFixed(8)}, lightDutyEmissions=${lightDutyEmissions.toFixed(2)}`);
        if (year === 2024) {
          // Get 2023 EMFAC emission factor for comparison
          policyTsDebug(`🔍 2024 vs 2023: 2023_emissions=${ldv2023.toFixed(2)}, 2024_emissions=${lightDutyEmissions.toFixed(2)}, change=${((lightDutyEmissions - ldv2023) / ldv2023 * 100).toFixed(2)}%`);
          policyTsDebug(`🔍 2024 VMT: baselineVmt_2023=${baselineVmt.toFixed(0)}, forecasting_ratio_2024=${emfacYearRatio?.toFixed(6) || 'NULL'}, projectedVmt_2024=${projectedVmt.toFixed(0)}`);
          policyTsDebug(`🔍 2024 EF: yearEmissionFactor_county_2024=${yearEmissionFactorCounty.toFixed(8)}, adjustedEmissionFactor_2024=${adjustedEmissionFactor.toFixed(8)}, scaling_factor=${emissionFactorScalingFactor.toFixed(6)}`);
        }
      }
      
      // Log EV adoption and VMT reduction progress for debugging (log first few years and every 5 years)
      if (year <= 2027 || year % 5 === 0 || year === 2045) {
        policyTsDebug(`Year ${year}: VMT=${projectedVmt.toFixed(0)} (reduced=${reducedVmt.toFixed(0)}), VMT Reduction=${vmtReductionPercentThisYear.toFixed(2)}% (target: ${vmtReductionPercent}%, annual increase: ${annualVMTReductionIncrease.toFixed(3)}%/year), EV%=${evPercentThisYear.toFixed(2)}% (target: ${evAdoptionPercent}%, annual increase: ${annualEVIncrease.toFixed(3)}%/year), Fleet EF=${effectiveEmissionFactor.toFixed(6)} tons/mile (adjustedEF=${adjustedEmissionFactor.toFixed(6)}), Emissions=${lightDutyEmissions.toFixed(0)} tons CO2e`);
      }
      
      // For other sources:
      // - Waste scales by county population.
      // - Natural Gas and Electricity: use BAU pathway (therms, kWh, RNG) when provided.
      // - Heavy Duty + Offroad: scale by population and decarbonize 1.5%/year.
      const hdvOffroadDecarbFactor = Math.pow(0.985, year - 2023);

      const pop = countyPopByYear[year] || basePop;
      const popFactor = basePop > 0 ? pop / basePop : 1;

      let totalTherms: number;
      let buildingKwh: number;
      let naturalGasEmissions: number;
      if (buildingsBauPathway) {
        totalTherms = getPathwayTherms(buildingsBauPathway, year);
        buildingKwh = getPathwayKwh(buildingsBauPathway, year);
        const rngShare = getPathwayRngShare(buildingsBauPathway, year);
        naturalGasEmissions = (totalTherms * (1 - rngShare) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
      } else {
        totalTherms = calculateTotalNaturalGasConsumption(buildingsEnergyData!, year);
        buildingKwh = calculateTotalElectricityConsumption(buildingsEnergyData!, year);
        naturalGasEmissions = (totalTherms * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
      }
      const denom = Math.max(1, cleanElectricityYear - 2023);
      const t = clampNumber((year - 2023) / denom, 0, 1);
      const gPerKwh = g2023 * (1 - t);
      // EV charging kWh: add to electricity sector; subtract from LDV to avoid double-counting
      const evChargingKwh = reducedVmt * (evPercentThisYear / 100) * CONSTANTS.EV_KWH_PER_MILE;
      const totalKwh = buildingKwh + evChargingKwh;
      const electricityEmissions = (totalKwh * gPerKwh) / 1_000_000; // g -> metric tons
      const evEmissionsFromGrid = (evChargingKwh * gPerKwh) / 1_000_000;
      const lightDutyEmissionsAdj = Math.max(0, lightDutyEmissions - evEmissionsFromGrid);

      const agricultureScaled = Math.round((historical2023?.agricultureEmissions || 0) * popFactor);
      projectedData.push({
        year,
        lightDutyEmissions: Math.round(lightDutyEmissionsAdj),
        heavyDutyEmissions: Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
        naturalGasEmissions: Math.round(naturalGasEmissions),
        electricityEmissions: Math.round(electricityEmissions),
        wasteEmissions: Math.round((historical2023?.wasteEmissions || 0) * popFactor),
        offroadEmissions: Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor),
        agricultureEmissions: agricultureScaled,
        totalEmissions:
          Math.round(lightDutyEmissionsAdj) +
          Math.round((historical2023?.heavyDutyEmissions || 0) * popFactor * hdvOffroadDecarbFactor) +
          Math.round(naturalGasEmissions) +
          Math.round(electricityEmissions) +
          Math.round((historical2023?.wasteEmissions || 0) * popFactor) +
          Math.round((historical2023?.offroadEmissions || 0) * popFactor * hdvOffroadDecarbFactor) +
          agricultureScaled,
        evChargingKwh,
      });
    }
    
    policyTsDebug('Projected emissions calculated:', projectedData);
    return projectedData;
  } catch (error) {
    console.error('Error calculating projected emissions:', error);
    return [];
  }
}

/**
 * Calculate and cache baseline projections (expensive, done once per location)
 * This includes all the expensive database queries and calculations.
 * buildingsBauScenario drives stock-based BAU pathway for building therms/kWh/RNG.
 */
async function calculateAndCacheBaseline(
  location: string,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauScenario: BuildingsBauScenario = 'current_policy'
): Promise<BaselineProjections> {
  policyTsDebug(`📊 Calculating baseline projections for ${location} using EMFAC${emfacVersion} buildingsBau=${buildingsBauScenario} (this may take a few seconds)...`);

  // Calculate historical emissions (2005-2023) using EMFAC version for LDV
  const historicalData = await calculateHistoricalEmissions(location, emfacVersion);

  // Fetch baseline data to get EV percentages
  const baseline = await fetchBaselineGHGData(location, emfacVersion);
  const currentEVPercent2023 = baseline.currentEVPercent2023 || 0;
  const currentEVPercent2045 = baseline.currentEVPercent || 0;

  // Get VMT data (includes user customizations)
  let geofips: number | null = null;
  try {
    await supabaseLocationService.initialize();
    geofips = await supabaseLocationService.getGeofips(location);
  } catch (error) {
    policyTsWarn('Could not get geofips for VMT data:', error);
  }
  const vmtData = await getVMTDataForGHG(location, geofips || undefined, emfacVersion);
  const latestVmtData = vmtData.find(d => d.year === 2023);
  const baselineVmt = latestVmtData?.vmt || 0;

  // Get EMFAC forecasting data
  const emfacData = await fetchEMFACForecastingData(location, emfacVersion);

  // Get population data
  const countyPopRaw = await fetchCountyPopulationByYear(location);
  const countyPopByYear = fillAndExtrapolatePopulation(countyPopRaw, 2005, 2045);

  // Get buildings energy data and compute stock-based BAU pathway
  const buildingsEnergyData = await getBuildingsEnergyData(location);
  const buildingsBauPathway = getBuildingsBauPathway(buildingsEnergyData, buildingsBauScenario);

  // Get electricity carbon intensity series (gCO2e/kWh) for the location (Buildings page input)
  let electricityCarbonIntensityGPerKwhByYear: Record<number, number> = {};
  try {
    let geofipsForCI: number | null = null;
    try {
      await supabaseLocationService.initialize();
      geofipsForCI = await supabaseLocationService.getGeofips(location);
    } catch {
      geofipsForCI = null;
    }
    if (geofipsForCI) {
      const fromTable = await fetchElectricityCarbonIntensityMapForGeofips(
        supabaseClient,
        geofipsForCI,
        2005,
        2045
      );
      fromTable.forEach((v, y) => {
        electricityCarbonIntensityGPerKwhByYear[y] = v;
      });
      const ci = await fetchCarbonIntensityData(String(geofipsForCI));
      const years = (ci.chartData?.labels || []) as any[];
      const data = (ci.chartData?.datasets?.[0]?.data || []) as any[];
      years.forEach((y: any, i: number) => {
        const yr = Number(y);
        const v = Number(data[i]);
        if (
          Number.isFinite(yr) &&
          Number.isFinite(v) &&
          v > 0 &&
          electricityCarbonIntensityGPerKwhByYear[yr] == null
        ) {
          electricityCarbonIntensityGPerKwhByYear[yr] = v;
        }
      });
    }
  } catch (e) {
    policyTsWarn('Policy Tool: could not fetch carbon intensity series for baseline cache', e);
    electricityCarbonIntensityGPerKwhByYear = {};
  }
  
  // Calculate baseline projections (2024-2045) with NO policies
  // Note: 2023 is the baseline year and comes from historical data, not projections
  // IMPORTANT: For baseline BAU, we use emission factors from EMFAC directly (no EV adoption adjustment).
  // The emission factors from EMFAC already include the baseline EV adoption that EMFAC models.
  // We pass currentEVPercent2045 as both target and current to indicate "no change from baseline",
  // which will cause the code to use yearEmissionFactor directly without EV adjustments.
  policyTsDebug('🔍 calculateAndCacheBaseline: Calculating baseline BAU using EMFAC emission factors directly (no EV adjustment)');
  const baselineProjected = await calculateProjectedEmissions(
    location,
    historicalData,
    0,
    currentEVPercent2045,
    currentEVPercent2045,
    undefined,
    emfacVersion,
    buildingsBauPathway
  );
  policyTsDebug('🔍 calculateAndCacheBaseline: Baseline projected years:', baselineProjected.map(d => `${d.year}: LDV=${d.lightDutyEmissions}`).slice(0, 5));
  
  // Create hash of VMT data for cache invalidation (using hash functions from cache service)
  const { hashVMTData, hashBuildingsEnergyData } = await import('./policyBaselineCache');
  const vmtHash = hashVMTData(vmtData);
  const energyHash = hashBuildingsEnergyData(buildingsEnergyData);

  // Use historical 2023 from GHG Inventory chart directly (which uses VMT from VMT chart)
  // This ensures perfect continuity with historical data and respects user customizations
  // Projections start from 2024 using EMFAC forecasting ratios
  const historical2023 = historicalData.find(d => d.year === 2023);
  
  // Calculate emission factor scaling factor for city-specific adjustment
  // Formula: EF_city_2023 / EF_county_2023
  // This ensures continuity: 2023 uses city-specific EF, 2024+ uses county EF * scaling_factor
  let emissionFactorScalingFactor = 1.0;
  try {
    const ldv2023 = historical2023?.lightDutyEmissions || 0;
    if (ldv2023 > 0 && baselineVmt > 0) {
      // Calculate city-specific emission factor from GHG inventory: emissions / VMT
      const cityEmissionFactor2023 = ldv2023 / baselineVmt; // tons CO2 per mile
      
      // Get county-level emission factor for 2023 from EMFAC data
      const emfacInterp = buildEmfacInterpolator((emfacData || []) as EmfacForecastRow[]);
      const emfac2023Data = emfacInterp.getRow(2023) || emfacData.find(d => d.year === 2023);
      const countyEmissionFactor2023 = emfac2023Data?.ghg_per_mile;
      
      if (countyEmissionFactor2023 && countyEmissionFactor2023 > 0) {
        emissionFactorScalingFactor = cityEmissionFactor2023 / countyEmissionFactor2023;
        policyTsDebug(`🔍 Baseline Scaling Factor: ${emissionFactorScalingFactor.toFixed(6)} (city_2023=${cityEmissionFactor2023.toFixed(8)} / county_2023=${countyEmissionFactor2023.toFixed(8)})`);
      }
    }
  } catch (error) {
    policyTsWarn('Could not calculate emission factor scaling factor for baseline:', error);
  }
  
  // Baseline projections: 2023 (from GHG Inventory) + 2024-2045 (from projections)
  // The GHG Inventory chart already uses the correct VMT data from the VMT chart
  // Add evChargingKwh and make LDV ICE-only for 2023 so transport cost VMT back-solve is consistent
  const EV_KWH_PER_MILE = 0.3;
  const g2023 = electricityCarbonIntensityGPerKwhByYear[2023] || 300; // g CO2e/kWh
  const evChargingKwh2023 = baselineVmt * (currentEVPercent2023 / 100) * EV_KWH_PER_MILE;
  const evEmissions2023 = (evChargingKwh2023 * g2023) / 1_000_000; // MT
  const augmented2023 = historical2023
    ? {
        ...historical2023,
        evChargingKwh: evChargingKwh2023,
        lightDutyEmissions: Math.round(Math.max(0, (historical2023.lightDutyEmissions ?? 0) - evEmissions2023)),
        electricityEmissions: Math.round((historical2023.electricityEmissions ?? 0) + evEmissions2023),
        totalEmissions: historical2023.totalEmissions, // unchanged (LDV− + electricity+)
      }
    : null;
  const baselineProjectionsArray = augmented2023
    ? [augmented2023, ...baselineProjected]
    : baselineProjected;
  
  const baselineProjections: BaselineProjections = {
    location,
    vmtHash,
    energyHash,
    historicalData,
    baselineProjections: baselineProjectionsArray,
    baselineVMT: baselineVmt,
    currentEVPercent: currentEVPercent2023,
    currentEVPercent2045: currentEVPercent2045,
    solarCapacityKw: baseline.solarCapacityKw || 0,
    population: baseline.population || 0,
    emfacData,
    populationData: countyPopByYear,
    buildingsEnergyData,
    buildingsBauPathway,
    electricityCarbonIntensityGPerKwhByYear,
    emissionFactorScalingFactor,
    calculationDate: new Date()
  };

  const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
  setCachedBaseline(cacheKey, baselineProjections);
  
  policyTsDebug('✅ Baseline projections calculated and cached for', location);
  return baselineProjections;
}

/**
 * Apply policy transformations to cached baseline (instant, lightweight)
 * This performs only math operations - no database queries
 * @param options.rngOverridePercent - Optional RNG share 0–100 in 2045 only; model ramps from base-year share to this value over time
 * @param options.cop - Optional heat pump COP (2.5–4); default 3.5 (efficient unit in mild climate)
 */
export function applyPolicyTransformations(
  baseline: BaselineProjections,
  scenario: PolicyScenario,
  options?: { rngOverridePercent?: number; cop?: number; efficiencyLayerOptions?: EfficiencyLayerOptions }
): TimeSeriesEmissionData[] {
  const targetEVPercent = scenario.evAdoptionPercent || 0;
  const vmtReductionPercent = scenario.vmtReductionPercent || 0;
  const targetEnergyEfficiencyPercent = Math.max(0, Math.min(40, (scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0));
  const hdvScale = Math.max(0, 1 - (scenario.hdvElectrificationPercent || 0) / 100);
  const targetBuildingElectrificationPercent = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
  // Organics diversion lever is LOCAL incremental beyond BAU (state policy already embedded in BAU).
  // Percent points additional by 2045 (0–50). This is a simple proportional direct-emissions approach.
  const targetOrganicsDiversionPercent = Math.max(0, Math.min(50, (scenario as any).organicsDiversionPercent || 0));
  const cleanElectricityYear = Math.max(2025, Math.min(2045, (scenario as any).gridCleanElectricityYear ?? 2045));

  // Calculate grid intensity for EV adoption (if needed)
  const CONSTANTS = {
    BASELINE_GRID_INTENSITY: 0.2,
    ANNUAL_SOLAR_HOURS: 2000,
    SOLAR_DISPLACEMENT_FACTOR: 0.85,
    EV_KWH_PER_MILE: 0.3,
    ICE_IMPROVEMENT_FACTOR: 0.95,
  };
  
  // Get EMFAC data for emission factors
  const validEmissionFactors = baseline.emfacData.filter((d: { ghg_per_mile?: number }) => 
    d.ghg_per_mile !== undefined && d.ghg_per_mile !== null && d.ghg_per_mile > 0
  );
  const avgEmissionFactor = validEmissionFactors.length > 0 
    ? validEmissionFactors.reduce((sum: number, d: { ghg_per_mile?: number }) => 
        sum + (d.ghg_per_mile ?? 0), 0
      ) / validEmissionFactors.length
    : 0.0008;
  const emfacInterp = buildEmfacInterpolator((baseline.emfacData || []) as EmfacForecastRow[]);
  const ciByYear = baseline.electricityCarbonIntensityGPerKwhByYear || {};
  const ciFallback2023 = ciByYear[2023] || 300; // gCO2e/kWh fallback
  const baseG2023 = ciByYear[2023] || ciFallback2023;
  
  // Calculate grid intensity for EV charging using Clean Electricity Year logic
  // (linearly reduce from 2023 Buildings-page carbon intensity to 0 by cleanElectricityYear)
  const getGridIntensityForYear = (year: number): number => {
    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    return baseG2023 * (1 - t); // g CO2e/kWh
  };
  
  // TEMPORARY: Removed computeFleetEmissionFactorFromYearBase - using raw EMFAC emission factors directly
  
  // Track cumulative VMT as we apply ratios sequentially
  let currentVmt = baseline.baselineVMT;

  // Buildings: efficiency layer (segment-based) applied to BAU pathway
  const bauPathway = baseline.buildingsBauPathway;
  const years = baseline.baselineProjections.map((r: TimeSeriesEmissionData) => r.year);
  const bauThermsByYear = years.map((y: number) =>
    bauPathway ? getPathwayTherms(bauPathway, y) : calculateTotalNaturalGasConsumption(baseline.buildingsEnergyData, y)
  );
  const bauKwhByYear = years.map((y: number) =>
    bauPathway ? getPathwayKwh(bauPathway, y) : calculateTotalElectricityConsumption(baseline.buildingsEnergyData, y)
  );
  const bauResThermsForPolicy: number[] = [];
  for (let i = 0; i < years.length; i++) {
    const y = years[i];
    const { resTherms: rawRes } = getNaturalGasConsumptionBySector(baseline.buildingsEnergyData, y);
    const idxP = bauPathway?.years.indexOf(y) ?? -1;
    if (
      bauPathway?.stockElectrifiedResidentialByYear?.length &&
      bauPathway.electrifiableResidential != null
    ) {
      const sR = idxP >= 0 ? (bauPathway.stockElectrifiedResidentialByYear[idxP] ?? 0) : 0;
      const eR = bauPathway.electrifiableResidential;
      bauResThermsForPolicy.push(rawRes * (1 - eR * sR));
    } else if (bauPathway?.stockFossilByYear) {
      const sf = idxP >= 0 ? (bauPathway.stockFossilByYear[idxP] ?? 1) : 1;
      bauResThermsForPolicy.push(rawRes * sf);
    } else {
      bauResThermsForPolicy.push(rawRes);
    }
  }
  const efficiencyTargetPercentPolicy = Math.max(
    0,
    Math.min(40, (scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0)
  );
  const rawEff =
    efficiencyTargetPercentPolicy === 0
      ? getDefaultEnergyEfficiencyComponents()
      : scenario.energyEfficiencyComponents && hasAnyEfficiencyComponent(scenario.energyEfficiencyComponents)
        ? scenario.energyEfficiencyComponents
        : distributeEnergyEfficiencyTarget(
            efficiencyTargetPercentPolicy,
            scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()
          );
  const effectiveComponents = normalizeEnergyEfficiencyComponents(rawEff);
  const efficiencyResult = applyEfficiencyLayer(
    bauThermsByYear,
    bauKwhByYear,
    years,
    effectiveComponents,
    options?.efficiencyLayerOptions
  );
  const efficiencyThermsByYear = efficiencyResult.thermsByYear;
  const efficiencyKwhByYear = efficiencyResult.kwhByYear;
  const resolvedEndUsePolicy = resolveBuildingEndUseAssumptions(scenario);
  
  // Apply transformations to each projected year
  // IMPORTANT: 2023 is the baseline year - no policies should be applied to it
  policyTsDebug('🔍 applyPolicyTransformations: Starting transformations');
  policyTsDebug('🔍 Baseline currentEVPercent:', baseline.currentEVPercent);
  policyTsDebug('🔍 Target EV percent:', targetEVPercent);
  policyTsDebug('🔍 Baseline projections years:', baseline.baselineProjections.map((d: TimeSeriesEmissionData) => d.year));

  let loggedMissingForecastingRatioPolicy = false;
  const transformedProjections = baseline.baselineProjections.map((baseRow: TimeSeriesEmissionData) => {
    const year = baseRow.year;
    const yearsElapsed = year - 2023;
    
    // EV adoption ramping
    // The baseline BAU already ramps from currentEVPercent (2023, 6.01%) to currentEVPercent2045 (2045, 69.27%)
    // If target equals currentEVPercent2045 (slider at default), use baseline as-is
    // Otherwise, apply ADDITIONAL EV adoption above/below the baseline ramp
    const baselineEV2045 = baseline.currentEVPercent2045 || baseline.currentEVPercent; // 2045 BAU EV% (slider default)
    const additionalEVPercent = targetEVPercent - baselineEV2045; // Compare against 2045 BAU value, not 2023 value
    
    // Debug for 2023-2026 to understand what's happening
    if (year <= 2026) {
      policyTsDebug(`🔍 Year ${year}: yearsElapsed=${yearsElapsed}, additionalEVPercent=${additionalEVPercent.toFixed(2)}%, baseRow.lightDutyEmissions=${baseRow.lightDutyEmissions}`);
    }
    
    // Calculate EV% for this year
    // The baseline BAU ramps from currentEVPercent (2023) to currentEVPercent2045 (2045)
    // For policy scenarios, we add/subtract from the baseline ramp
    // Linear ramp: 2023 = 0 reduction, 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22 (full reduction)
    // Total reduction steps = 22 (2024-2045, excluding 2023)
    const totalReductionSteps = 2045 - 2023; // 22 steps (2024 to 2045)
    
    // Calculate ramp step: 2023 = 0, 2024 = 1, 2025 = 2, ..., 2045 = 22
    // 2023 has no reduction (baseline), reductions start in 2024
    // For 2023, yearsElapsed = 0, so rampStep = 0 (no reduction)
    // For 2024+, yearsElapsed >= 1, so rampStep = yearsElapsed (1, 2, ..., 22)
    const rampStep = year === 2023 ? 0 : Math.max(0, yearsElapsed); // 2023 = 0, 2024 = 1, ..., 2045 = 22
    
    // Calculate baseline EV% for this year (ramps from 2024 to 2045, not 2023)
    // Baseline BAU ramp: 2023 = currentEVPercent (no change), 2024 starts ramping, 2045 = currentEVPercent2045
    // For 2023, rampStep = 0, so baseline stays at currentEVPercent
    const baselineEVPercentThisYear = year === 2023
      ? baseline.currentEVPercent 
      : baseline.currentEVPercent + ((baselineEV2045 - baseline.currentEVPercent) / totalReductionSteps) * rampStep;
    
    // Calculate policy adjustment: additional EV% above/below baseline BAU
    // Reductions start in 2024 (rampStep = 1), 2023 has no reduction (rampStep = 0)
    // For 2023, rampStep = 0, so no policy adjustment is applied
    const annualEVIncrease = additionalEVPercent / totalReductionSteps;
    const evUnclamped = baselineEVPercentThisYear + (annualEVIncrease * rampStep);
    const evLowerBound = Math.min(baseline.currentEVPercent, targetEVPercent, baselineEV2045);
    const evUpperBound = Math.max(baseline.currentEVPercent, targetEVPercent, baselineEV2045);
    const evPercentThisYear = clampNumber(evUnclamped, Math.max(0, evLowerBound), Math.min(100, evUpperBound));
    const additionalEVPercentThisYear = evPercentThisYear - baselineEVPercentThisYear;
    
    // Debug log for baseline year (2023) and first few years to verify smooth ramping
    if (year <= 2026) {
      if (additionalEVPercent > 0) {
        policyTsDebug(`Policy Transform ${year}: additionalEVPercent=${additionalEVPercent.toFixed(2)}%, annualIncrease=${annualEVIncrease.toFixed(3)}%, additionalThisYear=${additionalEVPercentThisYear.toFixed(3)}%, evPercentThisYear=${evPercentThisYear.toFixed(2)}%`);
      } else {
        policyTsDebug(`Policy Transform ${year}: No additional EV adoption (baseline year or default slider)`);
      }
    }
    
    // VMT reduction ramping (same logic as EV adoption, uses same rampStep)
    // Linear ramp: 2023 = 0 reduction, 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22 (full reduction)
    const annualVMTReductionIncrease = vmtReductionPercent / totalReductionSteps;
    const vmtReductionPercentThisYear = Math.min(vmtReductionPercent, annualVMTReductionIncrease * rampStep);
    
    // If no additional EV adoption and no other policy changes, return baseline row unchanged
    // This ensures slider at default behaves exactly like zero - no drop in 2024
    // Check both the total policy and the year-specific reduction to catch edge cases
    if (additionalEVPercent === 0 && vmtReductionPercent === 0 && 
        (scenario.hdvElectrificationPercent || 0) === 0 &&
        targetBuildingElectrificationPercent === 0 &&
        targetEnergyEfficiencyPercent === 0 &&
        targetOrganicsDiversionPercent === 0 &&
        (scenario.solarCapacityMultiplier || 1) === 1 &&
        // Grid lever: only "no-op" when left at default 2045
        cleanElectricityYear === 2045) {
      if (year <= 2026) {
        policyTsDebug(`✅ Year ${year}: Returning baseline row unchanged (no policy changes)`);
      }
      return baseRow;
    }
    
    // Debug: If we're applying changes, log what's happening
    if (year <= 2026) {
      policyTsDebug(`⚠️ Year ${year}: Applying policy changes (additionalEVPercent=${additionalEVPercent.toFixed(2)}%, vmtReductionPercent=${vmtReductionPercent.toFixed(2)}%)`);
    }
    
    // Find EMFAC data for this year (for emission factor and VMT)
    const emfacYearData = emfacInterp.getRow(year) || baseline.emfacData.find((d: { year: number }) => d.year === year);
    const yearEmissionFactorCounty = emfacYearData?.ghg_per_mile || avgEmissionFactor;
    // Apply scaling factor to convert county-level emission factor to city-specific
    // Formula: EF_year_location = EF_year_county * (EF_2023_city / EF_2023_county)
    const emissionFactorScalingFactor = baseline.emissionFactorScalingFactor || 1.0;
    const adjustedEmissionFactor = yearEmissionFactorCounty * emissionFactorScalingFactor;
    // Use the adjusted (city-specific) emission factor as the base for EV policy calculations
    const emissionFactorForEvPolicy = adjustedEmissionFactor;
    
    // For projections (2024+), ALWAYS use forecasting ratios from emfac2025_county_filtered
    // Historical data is only to 2023. Everything after 2023 is a projection using ratios.
    // Never use actual VMT values for projections - they may have discontinuities.
    // forecasting_ratio for year Y = VMT_Y / VMT_(Y-1)
    // Applied as: VMT_Y = VMT_(Y-1) * forecasting_ratio_Y
    const emfacYearRatio = emfacYearData?.forecasting_ratio;
    let baselineVmt: number;
    
    if (year === 2023) {
      // 2023: use baseline VMT directly (historical data)
      baselineVmt = baseline.baselineVMT;
    } else if (emfacYearRatio && emfacYearRatio > 0) {
      // For 2024+: Always use forecasting ratio for projections
      baselineVmt = currentVmt * emfacYearRatio;
      currentVmt = baselineVmt; // Update for next iteration
    } else {
      if (!loggedMissingForecastingRatioPolicy) {
        loggedMissingForecastingRatioPolicy = true;
        policyTsWarn(
          '⚠️ EMFAC rows missing forecasting_ratio for some years (e.g. statewide California); VMT held flat for those years.',
        );
      }
      baselineVmt = currentVmt;
    }
    
    const reducedVmt = baselineVmt * (1 - vmtReductionPercentThisYear / 100);
    
    // Calculate LDV emissions with EV adoption policy
    // Scale linearly from baseline (currentEVPercent) to zero emissions (100% EV)
    const emissionFactor = emissionFactorForEvPolicy; // Adjusted (city-specific) emission factor
    let lightDutyEmissions: number;
    
    // 2023 should always use the GHG inventory value, regardless of policy settings
    // Reductions start in 2024 (1/22 of 2045 reduction), not 2023
    if (year === 2023) {
      lightDutyEmissions = baseRow.lightDutyEmissions || 0;
    } else if (additionalEVPercent === 0 && vmtReductionPercentThisYear === 0) {
      // No additional EV adoption and no VMT reduction - use baseline LDV emissions exactly
      lightDutyEmissions = baseRow.lightDutyEmissions || 0;
    } else if (additionalEVPercent === 0) {
      // No additional EV adoption but VMT reduction - adjust only for VMT reduction
      const baselineVmtRatio = baselineVmt > 0 ? reducedVmt / baselineVmt : 1;
      lightDutyEmissions = (baseRow.lightDutyEmissions || 0) * baselineVmtRatio;
    } else {
      // EV adoption differs from baseline - calculate fleet-weighted emission factor
      // IMPORTANT: We use adjustedEmissionFactor (city-specific, scaled from county-level EMFAC)
      // This emission factor already accounts for the baseline EV adoption in EMFAC's model
      // For policy scenarios where EV% differs from baseline, we need to adjust the emission factor
      
      // Use adjusted emission factor (city-specific, scaled) as the base
      // This represents the fleet-average emission factor from EMFAC (adjusted for city-specific scaling), which already includes
      // the baseline EV adoption in the model. For policy calculations, we adjust based on
      // the difference between target EV% and baseline EV%
      const rawEmissionFactor = emissionFactor; // Adjusted (city-specific) emission factor
      
      // Calculate grid intensity for EV charging
      const gridIntensityGPerKwh = getGridIntensityForYear(year);
      const gridIntensityKgPerKwh = gridIntensityGPerKwh / 1000;
      const evGhgPerMileTons = (gridIntensityKgPerKwh * CONSTANTS.EV_KWH_PER_MILE) / 1000;
      
      // Calculate fleet-weighted emission factor with LINEAR reduction
      // The reduction should be linear: 2024 = 1/22, 2025 = 2/22, ..., 2045 = 22/22
      // First, calculate what the baseline BAU EV% would be this year (ramps from 2023 to 2045)
      const baselineEV2023 = baseline.currentEVPercent; // 2023 baseline EV% (starting point)
      const baselineEV2045 = baseline.currentEVPercent2045 || baseline.currentEVPercent; // 2045 BAU EV%
      const targetEV2045 = targetEVPercent; // Target EV% in 2045 (from slider)
      
      // Calculate baseline BAU EV% for this year (ramps linearly from 2023 to 2045)
      const baselineEVThisYear = year === 2023
        ? baselineEV2023
        : baselineEV2023 + ((baselineEV2045 - baselineEV2023) / totalReductionSteps) * rampStep;
      
      // Calculate the total EV% increase needed from baseline BAU to target in 2045
      const totalEVIncreaseNeeded = targetEV2045 - baselineEV2045;
      
      // Apply linear ramp: rampStep / 22 of the total increase ON TOP OF baseline BAU
      // 2024: baseline BAU + (1/22) * total increase
      // 2025: baseline BAU + (2/22) * total increase
      // etc.
      const evPercentThisYearLinear = baselineEVThisYear + (totalEVIncreaseNeeded * rampStep / totalReductionSteps);
      
      // Clamp to valid range
      const evPercentFinal = Math.max(0, Math.min(100, evPercentThisYearLinear));
      
      // Calculate fleet-weighted emission factor based on actual EV percentage
      // The rawEmissionFactor represents the baseline fleet average at baselineEVThisYear
      // We need to calculate what the emission factor would be at evPercentFinal
      // 
      // The baseline emission factor at baselineEVThisYear is:
      // rawEmissionFactor = ICE_EF * (1 - baselineEVThisYear/100) + EV_EF * (baselineEVThisYear/100)
      // 
      // To get ICE_EF, we solve: ICE_EF = (rawEmissionFactor - EV_EF * baselineEVThisYear/100) / (1 - baselineEVThisYear/100)
      // Then calculate fleet EF at target: fleetEF = ICE_EF * (1 - evPercentFinal/100) + EV_EF * (evPercentFinal/100)
      
      const baselineEVFraction = baselineEVThisYear / 100;
      const targetEVFraction = evPercentFinal / 100;
      
      // Extract ICE emission factor from baseline fleet average
      // rawEmissionFactor = ICE_EF * (1 - baselineEVFraction) + EV_EF * baselineEVFraction
      // Solving for ICE_EF:
      const denominator = 1 - baselineEVFraction;
      const iceGhgPerMile = denominator > 0.001 // Avoid division by zero
        ? (rawEmissionFactor - evGhgPerMileTons * baselineEVFraction) / denominator
        : rawEmissionFactor; // Fallback if baseline is already 100% EV
      
      // Calculate fleet-weighted emission factor at target EV percentage
      const icePercent = 1 - targetEVFraction;
      let fleetEmissionFactor = (icePercent * iceGhgPerMile) + (targetEVFraction * evGhgPerMileTons);
      
      // Ensure emission factor doesn't go below EV-only emissions
      const finalEmissionFactor = Math.max(evGhgPerMileTons, fleetEmissionFactor);
      
      // Calculate emissions: VMT * fleet emission factor
      lightDutyEmissions = reducedVmt * finalEmissionFactor;
      
      // Debug log for first few years
      if (year <= 2026) {
        policyTsDebug(`🔍 EV Calc ${year}: baselineEVThisYear=${baselineEVThisYear.toFixed(2)}%, baselineEV2045=${baselineEV2045.toFixed(2)}%, targetEV2045=${targetEV2045.toFixed(2)}%, totalIncrease=${totalEVIncreaseNeeded.toFixed(2)}%, rampStep=${rampStep}, evPercentFinal=${evPercentFinal.toFixed(2)}%, rawEF=${rawEmissionFactor.toFixed(8)}, finalEF=${finalEmissionFactor.toFixed(8)}, Emissions=${lightDutyEmissions.toFixed(0)}`);
      }
    }
    
    // Apply population scaling and decarbonization factors
    // Buildings policy ramps (linear from 2023 -> 2045)
    // 2023 = 0, 2024 = 1/22, ..., 2045 = 22/22 (full implementation)
    const totalSteps = 2045 - 2023; // 22 steps (2024-2045, excluding 2023)
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const buildingElectrificationThisYear = targetBuildingElectrificationPercent * ramp;
    const organicsDiversionThisYear = targetOrganicsDiversionPercent * ramp;
    // Buildings: use pre-computed efficiency-adjusted therms/kWh (efficiency layer already applied)
    const yearIdx = years.indexOf(year);
    const baseTherms = yearIdx >= 0 ? efficiencyThermsByYear[yearIdx] ?? 0 : 0;
    const baseKwh = yearIdx >= 0 ? efficiencyKwhByYear[yearIdx] ?? 0 : 0;
    const BASE_YEAR = 2023;
    const TARGET_YEAR = 2045;
    let rngShare: number;
    if (options?.rngOverridePercent != null) {
      const target2045Share = options.rngOverridePercent / 100;
      const baseYearShare = bauPathway ? getPathwayRngShare(bauPathway, BASE_YEAR) : 0;
      const denom = TARGET_YEAR - BASE_YEAR;
      const t = year <= BASE_YEAR ? 0 : year >= TARGET_YEAR ? 1 : (year - BASE_YEAR) / denom;
      rngShare = clampNumber(baseYearShare + (target2045Share - baseYearShare) * t, 0, 1);
    } else {
      rngShare = bauPathway ? getPathwayRngShare(bauPathway, year) : 0;
    }

    const elecFrac = clampNumber(buildingElectrificationThisYear / 100, 0, 1);
    const bauTotPolicy = yearIdx >= 0 ? bauThermsByYear[yearIdx] ?? 0 : 0;
    const bauResPolicy = yearIdx >= 0 ? bauResThermsForPolicy[yearIdx] ?? 0 : 0;
    const electrifiablePoolPolicy = electrifiableGasThermsInEfficiencySlice(
      baseTherms,
      bauTotPolicy,
      bauResPolicy,
      resolvedEndUsePolicy
    );
    const thermsReplaced = electrifiablePoolPolicy * elecFrac;
    const thermsRemaining = Math.max(0, baseTherms - thermsReplaced);

    const kwhAfterEff = baseKwh;
    // kWh_per_therm = 29.3 / COP; electricityAdded_kwh = thermsReplaced * (29.3 / COP)
    const cop = Math.max(2.5, Math.min(4, options?.cop ?? 3.0));
    const addedKwh = thermsReplaced * (KWH_PER_THERM / cop);
    let buildingKwh = kwhAfterEff + addedKwh;

    // EV charging kWh: add to electricity sector; subtract from LDV to avoid double-counting
    const evChargingKwh = reducedVmt * (evPercentThisYear / 100) * CONSTANTS.EV_KWH_PER_MILE;
    const totalKwh = buildingKwh + evChargingKwh;

    const denom = Math.max(1, cleanElectricityYear - 2023);
    const t = clampNumber((year - 2023) / denom, 0, 1);
    const gPerKwhPolicy = baseG2023 * (1 - t);

    const naturalGasEmissionsPolicy =
      (thermsRemaining * (1 - rngShare) * NG_EMISSION_FACTOR_KG_PER_THERM) / 1000;
    const electricityEmissionsPolicy = (totalKwh * gPerKwhPolicy) / 1_000_000;
    const evEmissionsFromGrid = (evChargingKwh * gPerKwhPolicy) / 1_000_000;
    const lightDutyEmissionsAdj = Math.max(0, lightDutyEmissions - evEmissionsFromGrid);

    
      // Apply additional organics diversion (incremental beyond BAU) to waste emissions.
      // IMPORTANT: baseRow.wasteEmissions is BAU (already includes statewide policy trajectory).
      const organicsDiversionFrac = clampNumber(organicsDiversionThisYear / 100, 0, 0.5);
      const wasteEmissionsPolicy = (baseRow.wasteEmissions || 0) * (1 - organicsDiversionFrac);
      
      const agriculturePolicy = Math.round(baseRow.agricultureEmissions || 0);
      return {
      year,
      lightDutyEmissions: Math.round(lightDutyEmissionsAdj),
        heavyDutyEmissions: Math.round((baseRow.heavyDutyEmissions || 0) * hdvScale),
        naturalGasEmissions: Math.round(naturalGasEmissionsPolicy),
        electricityEmissions: Math.round(electricityEmissionsPolicy),
        wasteEmissions: Math.round(wasteEmissionsPolicy),
        offroadEmissions: Math.round((baseRow.offroadEmissions || 0)),
        agricultureEmissions: agriculturePolicy,
      totalEmissions: Math.round(lightDutyEmissionsAdj +
          (baseRow.heavyDutyEmissions || 0) * hdvScale +
          naturalGasEmissionsPolicy +
          electricityEmissionsPolicy +
          wasteEmissionsPolicy +
          (baseRow.offroadEmissions || 0) +
          agriculturePolicy),
        evChargingKwh,
      } as TimeSeriesEmissionData;
    });
  
  return transformedProjections;
}

/**
 * Building energy time series (BAU and policy) for cost and other downstream layers.
 * Reuses same physical pipeline: BAU → efficiency → electrification → demand response.
 * Does not include emissions (RNG/grid); use for bills and capex only.
 * Also returns residential/commercial therm series for electrification CAPEX ($/unit).
 */
export function getBuildingEnergyTimeSeries(
  baseline: BaselineProjections,
  scenario: PolicyScenario,
  options?: {
    rngOverridePercent?: number;
    cop?: number;
    efficiencyLayerOptions?: EfficiencyLayerOptions;
    /** When DEBUG_BUILDING_ELECTRIFICATION=1, used to print $/kWh and $/therm in trace (from getAssumptions). */
    debugEnergyPrices?: { electricityPricePerKwh: number; naturalGasPricePerTherm: number };
    debugLocationLabel?: string;
  }
): {
  years: number[];
  bauThermsByYear: number[];
  bauKwhByYear: number[];
  policyThermsByYear: number[];
  policyKwhByYear: number[];
  bauResThermsByYear: number[];
  bauComThermsByYear: number[];
  policyResThermsByYear: number[];
  policyComThermsByYear: number[];
} {
  const bauPathway = baseline.buildingsBauPathway;
  const bld = baseline.buildingsEnergyData;
  const years = baseline.baselineProjections.map((r: TimeSeriesEmissionData) => r.year);
  const bauThermsByYear = years.map((y: number) =>
    bauPathway ? getPathwayTherms(bauPathway, y) : calculateTotalNaturalGasConsumption(bld, y)
  );
  const bauKwhByYear = years.map((y: number) =>
    bauPathway ? getPathwayKwh(bauPathway, y) : calculateTotalElectricityConsumption(bld, y)
  );
  const resolvedEndUse = resolveBuildingEndUseAssumptions(scenario);

  // Residential/commercial BAU therms for electrification CAPEX ($/unit)
  const bauResThermsByYear: number[] = [];
  const bauComThermsByYear: number[] = [];
  const gasAnchorYear = bauPathway?.demandAnchorYear ?? 2023;
  for (let i = 0; i < years.length; i++) {
    const y = years[i];
    const gasYear = bauPathway && y >= gasAnchorYear ? gasAnchorYear : y;
    const { resTherms: rawRes, comTherms: rawCom } = getNaturalGasConsumptionBySector(bld, gasYear);
    const idxB = bauPathway?.years.indexOf(y) ?? -1;
    if (
      bauPathway?.stockElectrifiedResidentialByYear?.length &&
      bauPathway?.stockElectrifiedCommercialByYear?.length &&
      bauPathway.electrifiableResidential != null &&
      bauPathway.electrifiableCommercial != null
    ) {
      const sR = idxB >= 0 ? (bauPathway.stockElectrifiedResidentialByYear[idxB] ?? 0) : 0;
      const sC = idxB >= 0 ? (bauPathway.stockElectrifiedCommercialByYear[idxB] ?? 0) : 0;
      const eR = bauPathway.electrifiableResidential;
      const eC = bauPathway.electrifiableCommercial;
      bauResThermsByYear.push(rawRes * (1 - eR * sR));
      bauComThermsByYear.push(rawCom * (1 - eC * sC));
    } else if (bauPathway?.stockFossilByYear) {
      const sf = idxB >= 0 ? (bauPathway.stockFossilByYear[idxB] ?? 1) : 1;
      bauResThermsByYear.push(rawRes * sf);
      bauComThermsByYear.push(rawCom * sf);
    } else {
      bauResThermsByYear.push(rawRes);
      bauComThermsByYear.push(rawCom);
    }
  }

  // When efficiency target is 0%, use zero components so policy = BAU + electrification only (no efficiency or DR).
  // That ensures scenario electricity cost >= BAU when only building electrification is applied.
  const efficiencyTargetPercent = Math.max(0, Math.min(40, (scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0));
  const rawEffTs =
    efficiencyTargetPercent === 0
      ? getDefaultEnergyEfficiencyComponents()
      : scenario.energyEfficiencyComponents && hasAnyEfficiencyComponent(scenario.energyEfficiencyComponents)
        ? scenario.energyEfficiencyComponents
        : distributeEnergyEfficiencyTarget(
            efficiencyTargetPercent,
            scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()
          );
  const effectiveComponents = normalizeEnergyEfficiencyComponents(rawEffTs);
  const efficiencyResult = applyEfficiencyLayer(
    bauThermsByYear,
    bauKwhByYear,
    years,
    effectiveComponents,
    options?.efficiencyLayerOptions
  );
  const efficiencyThermsByYear = efficiencyResult.thermsByYear;
  const efficiencyKwhByYear = efficiencyResult.kwhByYear;

  const targetBuildingElectrificationPercent = Math.max(0, Math.min(100, scenario.heatPumpAdoptionPercent || 0));
  const cop = Math.max(2.5, Math.min(4, options?.cop ?? 3.0));

  const policyThermsByYear = years.map((year, yearIdx) => {
    const totalSteps = 2045 - 2023;
    const yearsElapsed = year - 2023;
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const buildingElectrificationThisYear = targetBuildingElectrificationPercent * ramp;
    const baseTherms = efficiencyThermsByYear[yearIdx] ?? 0;
    const elecFrac = clampNumber(buildingElectrificationThisYear / 100, 0, 1);
    const bauTot = bauThermsByYear[yearIdx] ?? 0;
    const bauRes = bauResThermsByYear[yearIdx] ?? 0;
    const electrifiablePool = electrifiableGasThermsInEfficiencySlice(baseTherms, bauTot, bauRes, resolvedEndUse);
    const thermsReplaced = electrifiablePool * elecFrac;
    return Math.max(0, baseTherms - thermsReplaced);
  });

  // Policy res/com: apply same electrification ramp proportionally (preserves res/com ratio from efficiency layer)
  const policyResThermsByYear: number[] = [];
  const policyComThermsByYear: number[] = [];
  for (let i = 0; i < years.length; i++) {
    const bauTot = bauThermsByYear[i] || 1;
    const resFrac = bauTot > 0 ? (bauResThermsByYear[i] ?? 0) / bauTot : 0.6;
    const comFrac = 1 - resFrac;
    policyResThermsByYear.push(policyThermsByYear[i] * resFrac);
    policyComThermsByYear.push(policyThermsByYear[i] * comFrac);
  }

  const policyKwhByYear = years.map((year, yearIdx) => {
    const totalSteps = 2045 - 2023;
    const yearsElapsed = year - 2023;
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const buildingElectrificationThisYear = targetBuildingElectrificationPercent * ramp;
    const baseTherms = efficiencyThermsByYear[yearIdx] ?? 0;
    const baseKwh = efficiencyKwhByYear[yearIdx] ?? 0;
    const elecFrac = clampNumber(buildingElectrificationThisYear / 100, 0, 1);
    const bauTotK = bauThermsByYear[yearIdx] ?? 0;
    const bauResK = bauResThermsByYear[yearIdx] ?? 0;
    const electrifiablePoolK = electrifiableGasThermsInEfficiencySlice(baseTherms, bauTotK, bauResK, resolvedEndUse);
    const thermsReplaced = electrifiablePoolK * elecFrac;
    const addedKwh = thermsReplaced * (KWH_PER_THERM / cop); // 29.3/COP kWh per therm
    return baseKwh + addedKwh;
  });

  if (DEBUG_BUILDING_ELECTRIFICATION) {
    const pE = options?.debugEnergyPrices?.electricityPricePerKwh;
    const pG = options?.debugEnergyPrices?.naturalGasPricePerTherm;
    const sampleYear = years.includes(2045) ? 2045 : years[years.length - 1] ?? 2023;
    const si = years.indexOf(sampleYear);
    const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

    const bauT = si >= 0 ? bauThermsByYear[si] ?? 0 : 0;
    const bauK = si >= 0 ? bauKwhByYear[si] ?? 0 : 0;
    const effT = si >= 0 ? efficiencyThermsByYear[si] ?? 0 : 0;
    const effK = si >= 0 ? efficiencyKwhByYear[si] ?? 0 : 0;
    const polT = si >= 0 ? policyThermsByYear[si] ?? 0 : 0;
    const polK = si >= 0 ? policyKwhByYear[si] ?? 0 : 0;

    const totalSteps = 2045 - 2023;
    const yearsElapsed = sampleYear - 2023;
    const ramp = clampNumber(yearsElapsed / totalSteps, 0, 1);
    const hpPctThisYear = targetBuildingElectrificationPercent * ramp;
    const elecFracS = clampNumber(hpPctThisYear / 100, 0, 1);
    const bauResS = si >= 0 ? bauResThermsByYear[si] ?? 0 : 0;
    const electrifiablePoolS = electrifiableGasThermsInEfficiencySlice(effT, bauT, bauResS, resolvedEndUse);
    const thermsReplacedS = electrifiablePoolS * elecFracS;
    const addedKwhS = thermsReplacedS * (KWH_PER_THERM / cop);
    const kwhAfterHp = effK + addedKwhS;

    const locTag = options?.debugLocationLabel ? `${options.debugLocationLabel} ` : '';
    _policyTsConsole.groupCollapsed(
      `[Building electrification trace] ${locTag}year=${sampleYear} heatPump2045%=${targetBuildingElectrificationPercent} COP=${cop} KWH_PER_THERM=${KWH_PER_THERM}`,
    );
    _policyTsConsole.log('Context', {
      location: options?.debugLocationLabel,
      efficiencyTargetPercent,
      heatPumpAdoption2045Percent: targetBuildingElectrificationPercent,
    });
    _policyTsConsole.log(
      'Note: [BAU] = raw BAU pathway (used as cost baselinePathway). [POLICY] = after efficiency layer + heat pump. Compare [POST_EFFICIENCY] to [BAU] when diagnosing bill drops.',
    );
    _policyTsConsole.log('[BAU] energy (cost baseline — raw pathway, no local efficiency/electrification)', {
      year: sampleYear,
      gasTherms: bauT,
      electricityKwh: bauK,
      heatingProxyTherms_gas: bauT,
    });
    _policyTsConsole.log('[POST_EFFICIENCY] base before heat pump (after applyEfficiencyLayer)', {
      year: sampleYear,
      gasTherms: effT,
      electricityKwh: effK,
      deltaVsRawBauTherms: effT - bauT,
      deltaVsRawBauKwh: effK - bauK,
    });
    _policyTsConsole.log('[END_USE] electrifiable gas shares (space+water)', {
      residentialElectrifiableShare: resolvedEndUse.electrifiableResidential,
      commercialElectrifiableShare: resolvedEndUse.electrifiableCommercial,
      residentialEndUse: resolvedEndUse.residential,
      commercialEndUse: resolvedEndUse.commercial,
    });
    _policyTsConsole.log('[POLICY] electrification conversion', {
      year: sampleYear,
      totalPostEfficiencyTherms: effT,
      electrifiableThermsInPool: electrifiablePoolS,
      heatPumpAdoptionThisYearPercent: hpPctThisYear,
      elecFracOfElectrifiablePool: elecFracS,
      electrifiedTherms: thermsReplacedS,
      formulaAddedKwh: `addedKwh = electrifiedTherms * (${KWH_PER_THERM} / COP)`,
      copUsed: cop,
      addedKwhFromGasDisplacement: addedKwhS,
      postEfficiencyBaseKwh: effK,
      policyKwhAfterHeatPump: kwhAfterHp,
      policyKwhFinal: polK,
    });
    _policyTsConsole.log('[POLICY] energy (final — cost policyPathway)', {
      year: sampleYear,
      gasTherms: polT,
      electricityKwh: polK,
    });
    if (pE != null && pG != null) {
      const bauGasCost = bauT * pG;
      const bauElecCost = bauK * pE;
      const polGasCost = polT * pG;
      const polElecCost = polK * pE;
      _policyTsConsole.log('[BAU] prices & operating cost (sample year)', {
        electricityPricePerKwh: pE,
        naturalGasPricePerTherm: pG,
        totalGasCost: bauGasCost,
        totalElectricityCost: bauElecCost,
        totalEnergyCost: bauGasCost + bauElecCost,
      });
      _policyTsConsole.log('[POLICY] prices & operating cost (sample year)', {
        electricityPricePerKwh: pE,
        naturalGasPricePerTherm: pG,
        totalGasCost: polGasCost,
        totalElectricityCost: polElecCost,
        totalEnergyCost: polGasCost + polElecCost,
      });
      _policyTsConsole.log('[DELTA] sample year (policy − BAU)', {
        deltaGasTherms: polT - bauT,
        deltaElectricityKwh: polK - bauK,
        deltaGasCost: polGasCost - bauGasCost,
        deltaElectricityCost: polElecCost - bauElecCost,
        deltaTotalOperatingCost: polGasCost + polElecCost - (bauGasCost + bauElecCost),
      });
    } else {
      _policyTsConsole.log(
        '[SKIP] annual $ — pass debugEnergyPrices from buildingsCostModel (getAssumptions) or set in options.',
      );
    }

    const totBauT = sum(bauThermsByYear);
    const totBauK = sum(bauKwhByYear);
    const totPolT = sum(policyThermsByYear);
    const totPolK = sum(policyKwhByYear);
    _policyTsConsole.log('[TOTALS] sum over all series years (building stock only)', {
      years: years.length,
      bauSumGasTherms: totBauT,
      bauSumKwh: totBauK,
      policySumGasTherms: totPolT,
      policySumKwh: totPolK,
      deltaSumGasTherms: totPolT - totBauT,
      deltaSumKwh: totPolK - totBauK,
    });
    if (pE != null && pG != null) {
      const tbGas = sum(bauThermsByYear.map((t) => t * pG));
      const tbElec = sum(bauKwhByYear.map((k) => k * pE));
      const tpGas = sum(policyThermsByYear.map((t) => t * pG));
      const tpElec = sum(policyKwhByYear.map((k) => k * pE));
      _policyTsConsole.log('[TOTALS] operating cost sum (therm*kWh prices × each year)', {
        bauTotalGasCost: tbGas,
        bauTotalElectricityCost: tbElec,
        policyTotalGasCost: tpGas,
        policyTotalElectricityCost: tpElec,
        deltaGasCost: tpGas - tbGas,
        deltaElectricityCost: tpElec - tbElec,
        deltaNetOperatingCost: tpGas + tpElec - (tbGas + tbElec),
      });
    }
    _policyTsConsole.groupEnd();
  }

  return {
    years,
    bauThermsByYear,
    bauKwhByYear,
    policyThermsByYear,
    policyKwhByYear,
    bauResThermsByYear,
    bauComThermsByYear,
    policyResThermsByYear,
    policyComThermsByYear,
  };
}

const FLAT_BASELINE_YEAR_MIN = 2023;
const FLAT_BASELINE_YEAR_MAX = 2045;

/**
 * Flat baseline: current technology projected with population growth only (no state or local policy).
 * Used for cost views "BAU costs" and "Total costs" (cost vs flat).
 * Returns time series 2023–2045 with each year = 2023 emissions × (population_year / population_2023).
 */
export function generateFlatBaselineTimeSeries(baseline: BaselineProjections): TimeSeriesEmissionData[] {
  const proj = baseline.baselineProjections;
  const row2023 = proj.find((r) => r.year === FLAT_BASELINE_YEAR_MIN);
  const pop = baseline.populationData;
  const pop2023 = pop[FLAT_BASELINE_YEAR_MIN] ?? pop[2023] ?? baseline.population;
  if (!row2023 || !pop2023 || pop2023 <= 0) {
    return proj.filter((r) => r.year >= FLAT_BASELINE_YEAR_MIN && r.year <= FLAT_BASELINE_YEAR_MAX);
  }
  const out: TimeSeriesEmissionData[] = [];
  for (let y = FLAT_BASELINE_YEAR_MIN; y <= FLAT_BASELINE_YEAR_MAX; y++) {
    const scale = (pop[y] ?? pop2023) / pop2023;
    out.push({
      year: y,
      lightDutyEmissions: (row2023.lightDutyEmissions ?? 0) * scale,
      heavyDutyEmissions: (row2023.heavyDutyEmissions ?? 0) * scale,
      naturalGasEmissions: (row2023.naturalGasEmissions ?? 0) * scale,
      electricityEmissions: (row2023.electricityEmissions ?? 0) * scale,
      wasteEmissions: (row2023.wasteEmissions ?? 0) * scale,
      offroadEmissions: (row2023.offroadEmissions ?? 0) * scale,
      agricultureEmissions: (row2023.agricultureEmissions ?? 0) * scale,
      totalEmissions: (row2023.totalEmissions ?? 0) * scale,
      evChargingKwh: (row2023.evChargingKwh ?? 0) * scale,
    });
  }
  return out;
}

/**
 * Main function to generate time series data for policy impact tool
 * Uses baseline cache for instant policy transformations.
 * buildingsBauScenario selects stock-based BAU pathway for building therms/kWh/RNG.
 */
export async function generatePolicyTimeSeriesData(
  location: string,
  scenario: PolicyScenario,
  emfacVersion: '2021' | '2025' = '2025',
  buildingsBauScenario: BuildingsBauScenario = 'current_policy',
  options?: {
    efficiencyLayerOptions?: EfficiencyLayerOptions;
    onTimeSeriesProgress?: (progress: PolicyLoadProgress) => void;
  }
): Promise<PolicyTimeSeriesResult> {
  try {
    policyTsDebug(`Generating policy time series data for ${location} EMFAC${emfacVersion} buildingsBau=${buildingsBauScenario}`);
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    let baseline = getCachedBaseline(cacheKey);
    // If cached baseline has no LDV (e.g. was computed before VMT loaded), invalidate so we recalculate and match GHG Inventory.
    if (baseline) {
      const hasLDV = baseline.historicalData.some((d: TimeSeriesEmissionData) => (d.lightDutyEmissions ?? 0) > 0);
      if (!hasLDV) {
        policyTsDebug(`📊 Cached baseline has no LDV; invalidating for ${location} so chart matches GHG Inventory`);
        clearBaselineCache(location);
        baseline = null;
      }
    }
    if (!baseline) {
      policyTsDebug(`📊 No cached baseline for buildingsBau=${buildingsBauScenario}, calculating...`);
      options?.onTimeSeriesProgress?.({
        stepIndex: 1,
        stepCount: 2,
        label: 'Building multi-year chart baseline (2005–2045)…',
      });
      baseline = await getOrCreateCalculationPromise(cacheKey, () =>
        calculateAndCacheBaseline(location, emfacVersion, buildingsBauScenario)
      );
    } else {
      policyTsDebug('⚡ Using cached baseline projections (instant)');
      options?.onTimeSeriesProgress?.({
        stepIndex: 1,
        stepCount: 2,
        label: 'Using saved chart baseline…',
      });
    }

    options?.onTimeSeriesProgress?.({
      stepIndex: 2,
      stepCount: 2,
      label: 'Applying your policy levers across years…',
    });
    
    // Apply policy transformations to cached baseline (instant, synchronous)
    policyTsDebug('⚡ Applying policy transformations (instant)...');
    const transformOptions = options?.efficiencyLayerOptions
      ? { efficiencyLayerOptions: options.efficiencyLayerOptions }
      : undefined;
    const transformedProjections = applyPolicyTransformations(baseline, scenario, transformOptions);
    
    // Combine historical (2005-2022) with baseline projections (2023-2045); baseline.baselineProjections already includes 2023
    const historicalUpTo2022 = baseline.historicalData.filter((d: TimeSeriesEmissionData) => d.year < 2023);
    
    // Baseline data: GHG inventory (2005-2022) + baseline projections (2023-2045) for full 2005-2045 series
    const baselineData = [...historicalUpTo2022, ...baseline.baselineProjections];
    
    // Projected with policies: historical (2005-2022) + transformed projections (2023-2045)
    const projectedWithPolicies = [...historicalUpTo2022, ...transformedProjections];
    
    return {
      baselineData,
      projectedData: projectedWithPolicies,
      location
    };
  } catch (error) {
    console.error('Error generating policy time series data:', error);
    throw error;
  }
}
